/* eslint-disable react/require-default-props */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { nanoid } from 'nanoid';
import { FormController } from '../form/FormController';
import { Stepper } from './Stepper';
import { FormLabel } from '../form/FormLabel';
import { FormHelpMessage } from '../form/FormHelpMessage';
import { FormStatusMessage } from '../form/FormStatusMessage';

const QuantityPicker = ({
  min,
  max,
  step,
  label,
  id: idProp,
  value: valueProp,
  disabled,
  optional,
  required,
  requiredMessage,
  minimumMessage,
  maximumMessage,
  ...other
}) => {

  const [value, setValue] = useState(valueProp);
  const [status, setStatus] = useState();
  const [statusMessage, setStatusMessage] = useState(null);

  const stepperChangeHandler = (newValue) => {
    setValue(newValue);
  };

  useEffect(() => {
    const currentValue = Number(value);
    if (required && value === '') {
      setStatus('error');
      setStatusMessage(requiredMessage);
    } else if (currentValue > max) {
      setStatus('error');
      setStatusMessage(maximumMessage);
    } else if (currentValue < min) {
      setStatus('warning');
      setStatusMessage(minimumMessage);
    } else {
      setStatus(null);
      setStatusMessage('');
    }
  }, [required, value, min, max, minimumMessage, maximumMessage, requiredMessage]);

  const id = idProp || nanoid();
  const formLabelId = label && id ? `${id}-label` : undefined;
  const statusMessageId = statusMessage && id ? `${id}-status-message` : undefined;

  return (
    <FormController
      disabled={disabled}
      status={status}
    >
      <FormLabel
        htmlFor={id}
        id={formLabelId}
        optional={optional}
        required={required}
      >
        {label}
      </FormLabel>

      <Stepper
        aria-describedby={statusMessageId}
        min={min}
        max={max}
        value={value}
        onChange={stepperChangeHandler}
        disabled={disabled}
        id={id}
        step={step}
        {...other}
      />
      {status === 'error' && (
        <FormStatusMessage status={status} id={statusMessageId}>
          {statusMessage}
        </FormStatusMessage>
      )}
      {status === 'warning' && (
        <FormHelpMessage id={statusMessageId}>
          {statusMessage}
        </FormHelpMessage>
      )}
    </FormController>
  );
};

QuantityPicker.displayName = 'QuantityPicker';

QuantityPicker.propTypes = {
  /**
   * Minimum value allowed using the left button
   */
  min: PropTypes.number.isRequired,
  /**
   * Maximum value allowed using the right button
   */
  max: PropTypes.number.isRequired,
  /**
   * Increment to be applied when using the buttons
   */
  step: PropTypes.number,
  /**
   * If true, the buttons and text input will be inactive
   */
  disabled: PropTypes.bool,
  /**
   * id given to the text input
   */
  id: PropTypes.string,
  /**
   * Label to use above the buttons
   */
  label: PropTypes.string,
  /**
   * If true, the text "(optional)" is displayed next to the label
   */
  optional: PropTypes.bool,
  /**
   * If true, the * symbol is displayed next to the label
   */
  required: PropTypes.bool,
  /**
   * `QuantityPicker` value.
   */
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /**
   * Message to show when `required` is `true` and the user deletes the value in the text input
   */
  requiredMessage: PropTypes.string,
  /**
   * Message to show when the user types in a number less than `min`
   */
  minimumMessage: PropTypes.string,
  /**
   * Message to show when the user types in a number greater than `max`
   */
  maximumMessage: PropTypes.string,
};

QuantityPicker.defaultProps = {
  step: 1,
  required: false,
  optional: false,
  disabled: false,
  requiredMessage: 'Required value',
  minimumMessage: 'Value is below minimum',
  maximumMessage: 'Value is above maximum'
};

export { QuantityPicker };